import React, { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { updateUser } from "../../features/userSlice";
import "./Profile.css";
import tg from "../../api/tg";
import axios from "../../api/axios";
import { translations, normalizeLang } from "../../i18n/index.js";

// const SUPPORTED_LANGS = ["en", "ru", "zh"];
// const LANG_LABELS = { en: "🇬🇧 English", ru: "🇷🇺 Русский", zh: "🇨🇳 中文" };

const formatDurationLabel = (value, hLabel = "ч.", mLabel = "мин.") => {
  const str = String(value);
  let hours, minutes;
  if (str.includes(".")) {
    const parts = str.split(".");
    hours = Number(parts[0]);
    minutes = Number(parts[1].padEnd(2, "0").slice(0, 2));
  } else {
    hours = Number(str);
    minutes = 0;
  }
  if (minutes === 0) return `${hours} ${hLabel}`;
  if (hours === 0) return `${minutes} ${mLabel}`;
  return `${hours} ${hLabel} ${minutes} ${mLabel}`;
};

const formatPhoneDisplay = (value) => {
  const digits = value.replace(/\D/g, "").replace(/^7/, "");
  const clean = digits.slice(0, 10);
  let formatted = "+7";
  if (clean.length > 0) formatted += ` (${clean.slice(0, 3)}`;
  if (clean.length >= 4) formatted += `) ${clean.slice(3, 6)}`;
  if (clean.length >= 7) formatted += `-${clean.slice(6, 8)}`;
  if (clean.length >= 9) formatted += `-${clean.slice(8, 10)}`;
  return formatted;
};

const Profile = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.data);
  const loading = useSelector((state) => state.user.loading);

  const [firstName, setFirstName] = useState("");
  const [phone, setPhone] = useState("");
  const [languageCode, setLanguageCode] = useState("en");
  const [isSaved, setIsSaved] = useState(false);

  const [visits, setVisits] = useState([]);
  const [visitsLoading, setVisitsLoading] = useState(false);
  const [visitsError, setVisitsError] = useState("");

  const initialized = useRef(false);

  useEffect(() => {
    if (!user || initialized.current) return;

    setFirstName(user.firstName || "");

    const dig = (user.phone || "")
      .replace(/\D/g, "")
      .replace(/^7/, "")
      .slice(0, 10);
    setPhone(dig);

    setLanguageCode(normalizeLang(user.languageCode));
    initialized.current = true;
  }, [user]);

  useEffect(() => {
    setIsSaved(false);
  }, [firstName, phone]);

  const lang = normalizeLang(languageCode);
  const t = translations[lang];

  useEffect(() => {
    const telegramId = user?.telegramId ?? tg?.initDataUnsafe?.user?.id;
    if (!telegramId) return;

    let cancelled = false;

    setVisitsLoading(true);
    setVisitsError("");

    axios
      .get(`/api/user/${telegramId}/visits`, { params: { status: "confirmed", limit: 20 } })
      .then((res) => {
        if (cancelled) return;
        const items = Array.isArray(res?.data?.items) ? res.data.items : [];
        setVisits(items);
      })
      .catch((err) => {
        if (cancelled) return;
        setVisits([]);
        setVisitsError(err?.response?.data?.message || err?.message || "Failed to load history");
      })
      .finally(() => {
        if (cancelled) return;
        setVisitsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [user?.telegramId]);

  const originalPhoneDigits = (user?.phone || "")
    .replace(/\D/g, "")
    .replace(/^7/, "")
    .slice(0, 10);

  const originalLang = normalizeLang(user?.languageCode);
  const isModified =
    firstName !== (user?.firstName || "") ||
    phone !== originalPhoneDigits ||
    normalizeLang(languageCode) !== originalLang;

  const buildUpdatePayload = (nextLang = lang) => {
    if (!user) return null;

    return {
      ...user,
      telegramId: user.telegramId ?? user.telegramID ?? user.id ?? user._id,
      firstName,
      phone: formatPhoneDisplay(phone),
      languageCode: normalizeLang(nextLang),
    };
  };

  const handleSave = () => {
    if (!user || loading) return;

    const payload = buildUpdatePayload(lang);
    if (!payload) return;

    const action = dispatch(updateUser(payload));

    // Attempt to handle the async thunk result if possible
    if (typeof action?.unwrap === "function") {
      action.unwrap()
        .then(() => setIsSaved(true))
        .catch(() => { });
    } else if (typeof action?.then === "function") {
      action.then((res) => {
        if (!res.error) setIsSaved(true);
      });
    }
  };

  // const handleLanguageClick = (code) => {
  //   if (!user || loading) return;

  //   const next = normalizeLang(code);
  //   if (next === lang) return;

  //   const prev = lang;
  //   setLanguageCode(next);

  //   const payload = buildUpdatePayload(next);
  //   if (!payload) return;

  //   const action = dispatch(updateUser(payload));

  //   if (typeof action?.unwrap === "function") {
  //     action.unwrap().catch(() => setLanguageCode(prev));
  //   }
  // };

  const avatarUrl = tg?.initDataUnsafe?.user?.photo_url;

  return (
    <div className="profileContainer">
      <div className="avatarCantainer">
        <img className="avatar" src={avatarUrl || "/img/avatar.jpg"} alt="User Avatar" />
      </div>

      <div className="profileContent">
        <div className="inputContent">
          <label>{t.firstNameLabel}</label>
          <input
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder={t.firstNamePlaceholder}
          />
        </div>

        <div className="inputContent">
          <label>{t.nicknameLabel}</label>
          <input type="text" value={`@${user?.username || ""}`} disabled />
        </div>

        <div className="inputContent">
          <label>{t.phoneLabel}</label>
          <input
            type="text"
            value={formatPhoneDisplay(phone)}
            onChange={(e) => {
              const dig = e.target.value
                .replace(/\D/g, "")
                .replace(/^7/, "")
                .slice(0, 10);
              setPhone(dig);
            }}
            placeholder={t.phonePlaceholder}
          />
        </div>

        <div className="buttonContent">
          <button
            onClick={handleSave}
            disabled={!isModified || loading}
            className={isModified && !loading ? "active" : "inactive"}
          >
            {loading ? t.saving : (isSaved && !isModified ? t.saved : t.save)}
          </button>

        </div>

        <div className="historyDescription">
          <span>{t.visitHistoryTitle}</span>
        </div>

        {visitsLoading && (
          <p style={{ marginTop: "10px", opacity: 0.6 }}>{t.visitHistoryLoading}</p>
        )}

        {!visitsLoading && visitsError && (
          <p style={{ marginTop: "10px", opacity: 0.6 }}>
            {t.visitHistoryError}: {visitsError}
          </p>
        )}

        {!visitsLoading && !visitsError && visits.length === 0 && (
          <p style={{ marginTop: "10px", opacity: 0.6 }}>{t.visitHistoryEmpty}</p>
        )}

        {!visitsLoading && !visitsError && visits.length > 0 && (
          <div className="visitList">
            {visits.map((v) => (
              <div key={v.id || `${v.reservationNumber || ""}-${v.date || ""}-${v.time || ""}`} className="visitCard">
                <div className="visitCardHeader">
                  <div className="visitCardTitle">
                    {v.date ? (
                      <>
                        <span>{v.date}</span>
                      </>
                    ) : (
                      <span>{t.visitHistoryTitle}</span>
                    )}
                  </div>
                </div>

                {v.roomName ? (
                  <div className="visitCardSubtitle">{v.roomName}</div>
                ) : null}

                {(v.time || v.guests != null || v.duration != null) ? (
                  <div className="visitCardMeta">
                    {v.time ? (
                      <span>
                        {t.visitTime}: <b>{v.time}</b>
                      </span>
                    ) : null}
                    {v.time && (v.guests != null || v.duration != null) ? <span className="visitCardMetaSep">·</span> : null}
                    {v.guests != null ? (
                      <span>
                        {t.visitGuests}: <b>{v.guests}</b>
                      </span>
                    ) : null}
                    {v.guests != null && v.duration != null ? <span className="visitCardMetaSep">·</span> : null}
                    {v.duration != null ? (
                      <span>
                        {t.visitDuration}: <b>{formatDurationLabel(v.duration, t.visitHoursShort, t.visitMinutesShort)}</b>
                      </span>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )}
        {/* <p style={{marginTop: "10px", opacity: "0.6"}}>{t.language}</p>
         <div className="langButtons">
            {SUPPORTED_LANGS.map((code) => (
              <button
                key={code}
                type="button"
                disabled={loading}
                className={`langBtn ${lang === code ? "langBtnActive" : ""}`}
                onClick={() => handleLanguageClick(code)}
              >
                {LANG_LABELS[code]}
              </button>
            ))}
          </div> */}

      </div>
    </div>
  );
};

export default Profile;